import { SYSTEM_EARNINGS } from '../data/hrDefaults'
import {
  AttendanceRecord,
  EmployeeLoan,
  EmployeePayroll,
  HrDepartment,
  HrEmployee,
  HrPosition,
  LeaveRequest,
  PayAdjustment,
  PayLine,
  PayrollSettings,
  PayrollTotals,
  SalaryStructure,
  StatutoryRuleSet,
  TaxBand,
} from '../types/hr'
import {
  employeeName,
  overlapWeekdays,
  periodBounds,
  toKwacha,
  toNgwee,
} from './hrFormat'
import { leaveSnapshot } from './leaveAccrual'

/** Rule set in force for a payroll month (latest effectiveFrom ≤ period). */
export function ruleSetForPeriod(
  ruleSets: StatutoryRuleSet[],
  period: string,
): StatutoryRuleSet | undefined {
  return [...ruleSets]
    .filter((rules) => rules.effectiveFrom <= period)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0]
}

/** Salary structure in force for the period (effective-dated history). */
export function salaryForPeriod(
  employee: HrEmployee,
  period: string,
): Pick<SalaryStructure, 'basicSalary' | 'earnings' | 'deductions'> {
  const entry = [...(employee.salaryHistory ?? [])]
    .filter((item) => item.effectiveFrom <= period)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0]
  return entry ?? {
    basicSalary: employee.basicSalary,
    earnings: employee.earnings ?? [],
    deductions: employee.deductions ?? [],
  }
}

/** Progressive monthly PAYE in ngwee. */
export function calculatePaye(chargeableNgwee: number, bands: TaxBand[]): number {
  let lower = 0
  let tax = 0
  for (const band of bands) {
    const upper = band.upTo === null ? Infinity : toNgwee(band.upTo)
    const portion = Math.max(0, Math.min(chargeableNgwee, upper) - lower)
    tax += (portion * band.rate) / 100
    lower = upper
    if (chargeableNgwee <= upper) break
  }
  return Math.round(tax)
}

const pct = (ngwee: number, rate: number) => Math.round((ngwee * rate) / 100)

export function isEligibleForPeriod(employee: HrEmployee, period: string) {
  const { start, end } = periodBounds(period)
  if (employee.status === 'Inactive') return false
  if (employee.startDate && employee.startDate > end) return false
  if (employee.status === 'Terminated') {
    return Boolean(employee.terminationDate && employee.terminationDate >= start)
  }
  return true
}

type EngineInput = {
  employee: HrEmployee
  period: string
  rules: StatutoryRuleSet
  settings: PayrollSettings
  departments: HrDepartment[]
  positions: HrPosition[]
  attendance: AttendanceRecord[]
  leave: LeaveRequest[]
  loans: EmployeeLoan[]
  adjustments: PayAdjustment[]
}

/**
 * Calculates one employee's payroll for a month.
 * GROSS = basic + allowances + attendance pay + variable earnings
 * TOTAL DEDUCTIONS = PAYE + NAPSA(ee) + NHIMA(ee) + other authorised deductions
 * NET = GROSS − TOTAL DEDUCTIONS. Employer costs are kept separate.
 */
export function calculateEmployeePayroll(input: EngineInput): EmployeePayroll {
  const { employee, period, rules, settings } = input
  const { start, end } = periodBounds(period)
  const earningType = (id: string) =>
    settings.earningTypes.find((type) => type.id === id)
  const deductionType = (id: string) =>
    settings.deductionTypes.find((type) => type.id === id)
  const salary = salaryForPeriod(employee, period)
  const warnings: string[] = []
  const errors: string[] = []

  const basicN = toNgwee(salary.basicSalary)
  const dailyN = basicN / Math.max(1, settings.workingDaysPerMonth)
  const hourlyN = dailyN / Math.max(1, settings.hoursPerDay)

  type Earning = { code: string; label: string; ngwee: number }
  const earnings: Earning[] = [
    { code: SYSTEM_EARNINGS.basic, label: 'Basic Salary', ngwee: basicN },
  ]
  const addEarning = (code: string, ngwee: number, suffix = '') => {
    if (!ngwee) return
    earnings.push({
      code,
      label: `${earningType(code)?.name ?? code}${suffix}`,
      ngwee: Math.round(ngwee),
    })
  }
  for (const item of salary.earnings) addEarning(item.typeId, toNgwee(item.amount))

  // Approved attendance in this month drives duty pay and absences.
  const shifts = input.attendance.filter(
    (record) =>
      record.employeeId === employee.id &&
      record.status === 'Approved' &&
      record.date >= start &&
      record.date <= end,
  )
  const count = (type: AttendanceRecord['shiftType']) =>
    shifts.filter((record) => record.shiftType === type).length
  const nights = count('Night')
  const weekends = count('Weekend')
  const onCalls = count('On-call')
  const holidays = count('Public Holiday')
  const overtimeHours = shifts
    .filter((record) => record.shiftType === 'Overtime')
    .reduce((sum, record) => sum + (Number(record.hours) || 0), 0)
  const absences = settings.deductAbsences ? count('Absent') : 0

  addEarning(SYSTEM_EARNINGS.nightDuty, nights * toNgwee(settings.nightDutyRate), ` (${nights} shift${nights === 1 ? '' : 's'})`)
  addEarning(SYSTEM_EARNINGS.weekendDuty, weekends * toNgwee(settings.weekendDutyRate), ` (${weekends} shift${weekends === 1 ? '' : 's'})`)
  addEarning(SYSTEM_EARNINGS.onCall, onCalls * toNgwee(settings.onCallRate), ` (${onCalls} duty)`)
  addEarning(SYSTEM_EARNINGS.publicHoliday, holidays * dailyN * settings.publicHolidayMultiplier, ` (${holidays} day${holidays === 1 ? '' : 's'})`)
  addEarning(SYSTEM_EARNINGS.overtime, overtimeHours * hourlyN * settings.overtimeMultiplier, ` (${overtimeHours} h)`)

  // Unpaid leave + absences reduce pay (capped at basic).
  const unpaidLeaveTypes = new Set(
    settings.leaveTypes.filter((type) => !type.paid).map((type) => type.id),
  )
  const unpaidDays = input.leave
    .filter(
      (request) =>
        request.employeeId === employee.id &&
        request.status === 'Approved' &&
        unpaidLeaveTypes.has(request.leaveTypeId),
    )
    .reduce(
      (sum, request) =>
        sum + overlapWeekdays(request.startDate, request.endDate, start, end),
      0,
    )
  const lostDays = unpaidDays + absences
  if (lostDays > 0) {
    const loss = Math.min(basicN, Math.round(lostDays * dailyN))
    earnings.push({
      code: SYSTEM_EARNINGS.unpaidLeave,
      label: `Unpaid leave / absence (${lostDays} day${lostDays === 1 ? '' : 's'})`,
      ngwee: -loss,
    })
  }

  // Variable earnings/deductions captured for this month.
  const adjustments = input.adjustments.filter(
    (item) => item.employeeId === employee.id && item.period === period,
  )
  for (const item of adjustments.filter((a) => a.kind === 'earning')) {
    addEarning(item.typeId, toNgwee(item.amount), item.note ? ` — ${item.note}` : '')
  }

  const grossN = Math.max(0, earnings.reduce((sum, item) => sum + item.ngwee, 0))
  const flagged = (flag: 'taxable' | 'pensionable') =>
    Math.max(
      0,
      earnings
        .filter((item) => earningType(item.code)?.[flag] ?? true)
        .reduce((sum, item) => sum + item.ngwee, 0),
    )
  const taxableEarningsN = flagged('taxable')
  const pensionableN = flagged('pensionable')

  // Other deductions (fixed + variable + loans).
  type Deduction = { code: string; label: string; ngwee: number; preTax: boolean }
  const other: Deduction[] = []
  for (const item of salary.deductions) {
    const type = deductionType(item.typeId)
    if (toNgwee(item.amount) > 0)
      other.push({
        code: item.typeId,
        label: type?.name ?? item.typeId,
        ngwee: toNgwee(item.amount),
        preTax: type?.preTax ?? false,
      })
  }
  for (const item of adjustments.filter((a) => a.kind === 'deduction')) {
    const type = deductionType(item.typeId)
    other.push({
      code: item.typeId,
      label: `${type?.name ?? item.typeId}${item.note ? ` — ${item.note}` : ''}`,
      ngwee: toNgwee(item.amount),
      preTax: type?.preTax ?? false,
    })
  }
  const loanRecoveries: EmployeePayroll['loanRecoveries'] = []
  for (const loan of input.loans) {
    if (
      loan.employeeId !== employee.id ||
      loan.status !== 'Active' ||
      loan.repaymentStart > period ||
      loan.repayments.some((repayment) => repayment.period === period)
    )
      continue
    const amountN = Math.min(toNgwee(loan.monthlyInstalment), toNgwee(loan.balance))
    if (amountN <= 0) continue
    loanRecoveries.push({ loanId: loan.id, type: loan.type, amount: toKwacha(amountN) })
    other.push({
      code: loan.type === 'Salary Advance' ? 'salary-advance' : 'loan',
      label: loan.type === 'Salary Advance' ? 'Salary Advance' : 'Loan Repayment',
      ngwee: amountN,
      preTax: false,
    })
  }

  // Statutory.
  const preTaxN = other.filter((d) => d.preTax).reduce((s, d) => s + d.ngwee, 0)
  const chargeableN = Math.max(0, taxableEarningsN - preTaxN)
  const payeN = calculatePaye(chargeableN, rules.payeBands)
  const napsaBaseN = Math.min(pensionableN, toNgwee(rules.napsaMonthlyCeiling))
  const napsaEeN = pct(napsaBaseN, rules.napsaEmployeeRate)
  const napsaErN = pct(napsaBaseN, rules.napsaEmployerRate)
  const unpaidN = -(earnings.find((e) => e.code === SYSTEM_EARNINGS.unpaidLeave)?.ngwee ?? 0)
  const nhimaBaseN =
    rules.nhimaBasis === 'basic' ? Math.max(0, basicN - unpaidN) : grossN
  const nhimaEeN = pct(nhimaBaseN, rules.nhimaEmployeeRate)
  const nhimaErN = pct(nhimaBaseN, rules.nhimaEmployerRate)
  const sdlN = pct(grossN, rules.sdlRate)
  const wcN = pct(grossN, rules.workersCompRate)

  const otherN = other.reduce((sum, item) => sum + item.ngwee, 0)
  const totalDeductionsN = payeN + napsaEeN + nhimaEeN + otherN
  let netN = grossN - totalDeductionsN
  if (settings.netPayRounding === 'nearest-kwacha') netN = Math.round(netN / 100) * 100
  const employerCostN = grossN + napsaErN + nhimaErN + sdlN + wcN

  const department = input.departments.find((d) => d.id === employee.departmentId)
  const position = input.positions.find((p) => p.id === employee.positionId)

  if (basicN <= 0) errors.push('No basic salary set')
  if (netN < 0) errors.push('Deductions exceed gross pay')
  if (!employee.bankAccountNumber) warnings.push('Bank account missing')
  if (!employee.napsaNumber) warnings.push('NAPSA number missing')
  if (!employee.nhimaNumber) warnings.push('NHIMA number missing')

  const line = (code: string, label: string, ngwee: number): PayLine => ({
    code,
    label,
    amount: toKwacha(ngwee),
  })

  return {
    employeeId: employee.id,
    employeeNumber: employee.employeeNumber,
    name: employeeName(employee),
    departmentId: employee.departmentId,
    departmentName: department?.name ?? 'Unassigned',
    positionTitle: position?.title ?? 'Unassigned',
    category: position?.category ?? department?.category ?? 'Support',
    bankName: employee.bankName,
    bankBranch: employee.bankBranch,
    bankAccountName: employee.bankAccountName,
    bankAccountNumber: employee.bankAccountNumber,
    napsaNumber: employee.napsaNumber,
    nhimaNumber: employee.nhimaNumber,
    tpin: employee.tpin,
    basic: toKwacha(basicN),
    allowances: toKwacha(grossN - basicN),
    earnings: earnings.map((item) => line(item.code, item.label, item.ngwee)),
    deductions: [
      line('paye', 'PAYE', payeN),
      line('napsa', `NAPSA (${rules.napsaEmployeeRate}%)`, napsaEeN),
      line('nhima', `NHIMA (${rules.nhimaEmployeeRate}%)`, nhimaEeN),
      ...other.map((item) => line(item.code, item.label, item.ngwee)),
    ],
    employer: [
      line('napsa-er', `NAPSA employer (${rules.napsaEmployerRate}%)`, napsaErN),
      line('nhima-er', `NHIMA employer (${rules.nhimaEmployerRate}%)`, nhimaErN),
      line('sdl', `Skills Development Levy (${rules.sdlRate}%)`, sdlN),
      ...(rules.workersCompRate > 0
        ? [line('wc', `Workers' Compensation (${rules.workersCompRate}%)`, wcN)]
        : []),
    ],
    gross: toKwacha(grossN),
    taxable: toKwacha(chargeableN),
    paye: toKwacha(payeN),
    napsaEmployee: toKwacha(napsaEeN),
    napsaEmployer: toKwacha(napsaErN),
    nhimaEmployee: toKwacha(nhimaEeN),
    nhimaEmployer: toKwacha(nhimaErN),
    sdl: toKwacha(sdlN),
    workersComp: toKwacha(wcN),
    otherDeductions: toKwacha(otherN),
    totalDeductions: toKwacha(totalDeductionsN),
    net: toKwacha(netN),
    employerCost: toKwacha(employerCostN),
    loanRecoveries,
    adjustmentIds: adjustments.map((item) => item.id),
    leave: leaveSnapshot(employee, input.leave, settings, period),
    warnings,
    errors,
  }
}

export function totalPayroll(lines: EmployeePayroll[]): PayrollTotals {
  const sum = (key: keyof PayrollTotals & keyof EmployeePayroll) =>
    toKwacha(lines.reduce((total, line) => total + toNgwee(line[key] as number), 0))
  return {
    employees: lines.length,
    basic: sum('basic'),
    allowances: sum('allowances'),
    gross: sum('gross'),
    paye: sum('paye'),
    napsaEmployee: sum('napsaEmployee'),
    napsaEmployer: sum('napsaEmployer'),
    nhimaEmployee: sum('nhimaEmployee'),
    nhimaEmployer: sum('nhimaEmployer'),
    sdl: sum('sdl'),
    workersComp: sum('workersComp'),
    otherDeductions: sum('otherDeductions'),
    totalDeductions: sum('totalDeductions'),
    net: sum('net'),
    employerCost: sum('employerCost'),
  }
}

/** Leave balance for a leave type in a calendar year. */
export function leaveBalance(
  employee: HrEmployee,
  leaveType: { id: string; entitlementDays: number; accruesMonthly: boolean },
  requests: LeaveRequest[],
  year: number,
  today = new Date(),
) {
  let entitlement = leaveType.entitlementDays
  if (leaveType.accruesMonthly) {
    const start = new Date(`${employee.startDate || `${year}-01-01`}T00:00:00`)
    const from = start.getFullYear() < year ? new Date(year, 0, 1) : start
    const to = today.getFullYear() > year ? new Date(year, 11, 31) : today
    const months = Math.max(
      0,
      (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth(),
    )
    entitlement = Math.min(
      leaveType.entitlementDays,
      Math.round(((leaveType.entitlementDays / 12) * months) * 10) / 10,
    )
  }
  const taken = requests
    .filter(
      (request) =>
        request.employeeId === employee.id &&
        request.leaveTypeId === leaveType.id &&
        request.status === 'Approved' &&
        request.startDate.startsWith(String(year)),
    )
    .reduce((sum, request) => sum + request.days, 0)
  return { entitlement, taken, remaining: Math.round((entitlement - taken) * 10) / 10 }
}
